import React, { SyntheticEvent, useEffect, useState } from "react"
import '../../../styles/ProductCard.css';
import {Card} from "@vitalsource/vstui";
import {ICircle} from "@vitalsource/vstui-icons";
import {Popover} from "@vitalsource/vstui/labs"
import {angularRouterHost} from "../../utils/angularManagerUtils";
import Tooltip from "@vitalsource/vstui/Tooltip";
import styled from "styled-components";
import Dotdotdot from "react-dotdotdot";
import ProductEditPanel from "../ProductEditPanel";
import ProductCtrlPanel from "./ProductCtrlPanel";
import {ProductData} from "../../types/StudioAPITypes";
import LoadingSpinner from "../LoadingSpinner"
import {PRODUCT_STATUS, COLORS} from "../../utils/constants";
import {checkTitleDupAndModify} from "../../utils/helperUtils";
import ProductProgressIndicatorContainer from "../../containers/ProductProgressIndicatorContainer";

let ProductTitle = styled.div`
  position: relative;
  font-family: "Roboto";
  width:192px;
  height: 38px;
  font-weight: bold;
  font-size: 16px;
  color: #1B1B26;
  text-align: left;
  min-width: 0; 
  `;

let ProductLastEdited = styled.div`
  font-family: "Roboto";
  font-size: 16px;
  color: #757575;
  text-align: left;
  padding-top: 8px;
  cursor: default;`;

let ProductCardElem = styled(Card)`
    width: 224px;
    height: 394px;
    margin: 16px;
    border-radius: 4px;
    position: relative;
    overflow: visible;
    ` as any;

let ICircleCustom = styled(ICircle)` 
  position: relative;
  top: calc(50% - 50px);
  height: 100px; 
  width: 100px;
`;

const coverContainerAspectRatio = 0.823; // -- width of the cover container / height of the cover container

const spinnerStyles: React.CSSProperties = {
  position: 'absolute',
  color: 'white',
  zIndex: 1
};

export interface ProductCardProps {
  id: string;
  projectId: string;
  name: string;
  coverUrl: string
  modifiedDate: string;
  status: string;
  products: string[];
  inEditMode: boolean;
  numberOfScreens: number;
  publishDate: string;
  viewMode?: string;
  deleteProduct: (projectId: string, productId: string) => void;
  updateProduct: (projectId: string, productId: string, data: ProductData) => void;
  editProduct: (productId: string) => void;
  duplicateProduct: (productId: string, data: ProductData) => void;
}

interface WithLinkProps {
  children: React.ReactNode;
  className: string;
  link: string;
  status: string;
}
interface Product {
  id: string;
  status: string;
}

const WithLink = (props: WithLinkProps) => {
  if (props.status !== PRODUCT_STATUS.ACTIVE) {
    return (
      <div className={props.className}>
        {props.children}
      </div>
    )
  }

  return (
    <a className={props.className} href={props.link}>
      {props.children}
    </a>
  );
}

const ProductCard = (props: ProductCardProps) => {

//#region FLOE-5919 and 6842

  const [currentStatus, setCurrentStatus] = useState(props.status);
  const [hasFetchedOnce, setHasFetchedOnce] = useState(false);

const isProcessInProgress = (status: string): boolean =>
  [
    PRODUCT_STATUS.DUPLICATION_IN_PROGRESS,
    PRODUCT_STATUS.CONVERSION_IN_PROGRESS,
    PRODUCT_STATUS.TOC_LOCKED,
    PRODUCT_STATUS.IMPORT_IN_PROGRESS,
  ].includes(status);

  const processInProgress = isProcessInProgress(currentStatus) && hasFetchedOnce;
   
  useEffect(() => {
    if (props.status !== currentStatus) {
      setCurrentStatus(props.status);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.status]);

  // Poll only when currentStatus is in progress
  useEffect(() => {
    let intervalId: NodeJS.Timeout | undefined;

    if (isProcessInProgress(currentStatus)) {
      (async () => {
        try {
          await getProductDetails();
          setHasFetchedOnce(true);
        } catch (error) {
          // Error is already logged in getProductDetails
        }
      })();
      intervalId = setInterval(() => {
        getProductDetails();
      }, 20000);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentStatus]);

  const getProductDetails = async () => {
    try {
      const url = new URL(`/api/projects/${props.projectId}`, window.location.origin);
      url.searchParams.append('automatedRequest', 'true');
      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      if (data && data.data && Array.isArray(data.data.products)) {
        const productIndex = data.data.products.findIndex((p: { id: string; }) => p.id === props.id);
        if (productIndex !== -1) {
          const product = data.data.products[productIndex];
          if (product.status !== currentStatus) {
          setCurrentStatus(product.status);
        }
        }
      }
    } catch (error) {
      console.error('Fetch error:', error);
    }
  };
//#endregion FLOE-5919 and 6842

  const getFormattedDate = (dateString: string, showFullDate?: boolean): string => {
    // returns the date in string format MMM DD ?(YYYY). The year will be displayed if it is before the current year.
    var currentYear = new Date().getFullYear();
    var modifiedDate = new Date(dateString);

    if ((currentYear === modifiedDate.getFullYear()) && !showFullDate) {
      return modifiedDate.toLocaleDateString("en-us", {month: "short", day: "numeric"}).toString();
    } else {
      return modifiedDate.toLocaleDateString("en-us", {month: "short", day: "numeric", year:"numeric"}).toString();

    }
  };

  const renderCoverImage = function(coverUrl: string, productStatus: string) {
    if (productStatus === PRODUCT_STATUS.CONVERSION_FAILED) {
      return (
        <div className={"product-error-cover"}>
          <ICircleCustom color={COLORS.WHITE}></ICircleCustom>
        </div>
      )
    } else {
      return (
        <div>
          <img className="product-cover-distorted" src={coverUrl}></img>
          <img id="coverImage" className={"product-cover"}  src={coverUrl} onLoad={updateCoverPosition}></img>
        </div>
      )
    }
  }

  const getProductCloneName = (productName: string, products: any[]) => {

    var cpyReg = `(\\s)(Copy)(\\s\\d*)?$`;
    var cpyRegOpt = `(\\s?)(Copy)?(\\s\\d*)?`;

    var cpyRegExp = RegExp(cpyReg);


    // Check to see if this is formatted as a copy already
    productName = cpyRegExp.test(productName)? productName.replace(cpyRegExp, '') : productName;

    var similarTitlesReg = RegExp(`^(${productName})${cpyRegOpt}$`,'i');

    var matchingTitles = products.filter( (productName) => {
      return similarTitlesReg.test(productName);
    });

    // The length may not match the current copy iteration in the case of a delete. Double check to see if
    // the product exist - increment if it does.
    return checkTitleDupAndModify(productName, 'Copy', matchingTitles.length, matchingTitles);
  };

  const getInProgressMessage = (status: string) => {
    if (status)
      return status
        .replace("Conversion", "Import") // Replace if needed
        .replace(PRODUCT_STATUS.TOC_LOCKED, PRODUCT_STATUS.IMPORT_IN_PROGRESS) //FLOE-5918
        .concat("...");
  };

  const updateCoverPosition = function(e: SyntheticEvent<HTMLImageElement>) {
    let coverImage = e.target as HTMLImageElement;
    if (coverImage) {

      let naturalHeight = coverImage.naturalHeight || 1; // If natualHeight is undefined, aspect ratio width will be 0
      let naturalWidth = coverImage.naturalWidth || 0;
      let coverAspectRatio = naturalWidth/naturalHeight;

      // Cover is centered horizontally by default. Center vertically if the aspect ratio is flipped
      if (coverAspectRatio > coverContainerAspectRatio) {
        coverImage.setAttribute("class", coverImage.getAttribute("class") +  " cover-center__vertical");
      }
    }
  };

  if (!props.id) {
    return (
      <ProductCardElem className="product-card">
        <div className="product-cover-container nil"/>
        <div className="product-info product-info--nil">
          <div className="product-title product-info-large-nil"></div>
          <div className="product-title product-info-small-nil"></div>
          <div className="product-title product-info-large-nil"></div>
        </div>
      </ProductCardElem>
    )
  }
  
  const isListView = props.viewMode === "list";
  const canNavigateRow = isListView
    && currentStatus === PRODUCT_STATUS.ACTIVE
    && !props.inEditMode;

  const handleRowClick = () => {
    window.location.href = `${angularRouterHost()}${props.projectId}/${props.id}`;
  };

  const stopPropagation = (e: React.MouseEvent) => e.stopPropagation();

return (
    <ProductCardElem
      id={props.id}
      className="product-card"
      interactive
      raised
      onClick={canNavigateRow ? handleRowClick : undefined}
      style={canNavigateRow ? { cursor: "pointer" } : undefined}>
      <div className="product-cover-container">
        <ProductProgressIndicatorContainer id={props.id}>
          <LoadingSpinner loading={processInProgress}
                          message={processInProgress ? getInProgressMessage(currentStatus) : ""}
                          styles={spinnerStyles}>
            <WithLink className={"product-card-link"} link={`${angularRouterHost()}${props.projectId}/${props.id}`} status={currentStatus}>
                {renderCoverImage(props.coverUrl, currentStatus)}
          </WithLink>
        </LoadingSpinner>
        </ProductProgressIndicatorContainer>
      </div>
      <div className="product-info">
        <Tooltip placement="top-start"
          title={props.name}>
            <ProductTitle className="product-title" onClick={(e: React.MouseEvent) => {
              if (isListView) { e.stopPropagation(); }
              if (props.status === PRODUCT_STATUS.ACTIVE) {
                props.editProduct(props.id)
              }}}>
              {props.name.trim().includes(" ") ?
                <Dotdotdot clamp={2}>
                  {props.name}
                </Dotdotdot>:
                props.name}
          </ProductTitle>
        </Tooltip>
        <Tooltip placement="bottom"
                 title={getFormattedDate(props.modifiedDate, true)}>
          <ProductLastEdited className="product-last-edited" onClick={isListView ? stopPropagation : undefined}>Edited {getFormattedDate(props.modifiedDate)}</ProductLastEdited>
        </Tooltip>
      </div>
      {props.inEditMode ? "" : <div onClick={isListView ? stopPropagation : undefined}><ProductCtrlPanel
        status={currentStatus}
        productName={props.name}
        numberOfScreens={props.numberOfScreens}
        publishDate={props.publishDate}
        deleteProduct={() => props.deleteProduct(props.projectId, props.id)}
        editProduct={() => props.editProduct(props.id)}
        duplicateProduct={() => props.duplicateProduct(props.projectId, {
            name: getProductCloneName(props.name, props.products),
            clone: {
              srcProduct: props.id,
              srcProject: props.projectId
            }
          }
        )}
      /></div>}
      {props.inEditMode ? <ProductEditPanel
        editProduct={props.editProduct}
        updateProduct={(data: ProductData) => props.updateProduct(props.projectId, props.id, data)}
        name={props.name}
        products={props.products}/> : ""}
    </ProductCardElem>
  )
}

export default ProductCard;
